import { useState, useEffect, useMemo } from 'react';
import * as XLSX from 'xlsx';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { hasPermission } from '../utils/permissions';
import { formatDateDMY } from '../utils/dateFormat';
import SupplierLedgerTab from '../components/supplier_payments/SupplierLedgerTab';
import CreditorsAgingTab from '../components/supplier_payments/CreditorsAgingTab';

/**
 * Supplier Payments — outgoing payments to suppliers. Conceptually mirrors
 * Customer Payments but with multi-currency conversion + multi-PO split.
 *
 * A single payment can pay against N Purchase Orders (`lines[]`). Each line
 * is in the supplier's currency; the company-currency total is derived
 * from the per-payment exchange rate.
 */
const blankLine = () => ({
  po_id: '',
  po_number: '',
  po_currency: '',
  po_total: 0,
  amount_supplier_ccy: '',
  note: '',
});

const blankForm = () => ({
  supplier_id: '',
  supplier_name: '',
  supplier_ccy: 'USD',
  received_date: new Date().toISOString().slice(0, 10),
  payment_mode: 'Bank Transfer',
  exchange_rate: '',
  bank_fee_company_ccy: 0,
  supplier_bill_no: '',
  receipt_no: '',
  cheque_no: '',
  cheque_date: '',
  bank_name: '',
  transfer_date: '',
  reference: '',
  notes: '',
  lines: [blankLine()],
  // Advance support — defaults match the existing PO-payment behaviour.
  payment_type: 'po_payment',
  advance_amount_supplier_ccy: '',
});

const SupplierPayments = () => {
  const { user } = useAuth();
  const { companySettings, formatAmount } = useCurrency();
  const companyCcy = companySettings?.currency || 'BHD';

  const [payments, setPayments] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [openPOs, setOpenPOs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(blankForm());
  // Advance-application modal state
  const [applyAdv, setApplyAdv] = useState(null); // the advance row being applied
  const [applyLines, setApplyLines] = useState([{ po_id: '', amount_supplier_ccy: '' }]);
  const [filter, setFilter] = useState({ supplier_id: '', start_date: '', end_date: '' });
  const [activeTab, setActiveTab] = useState('payments'); // 'payments' | 'ledger' | 'creditors'

  const canCreate = hasPermission(user, 'supplier_payments', 'create');
  const canEdit = hasPermission(user, 'supplier_payments', 'edit');
  const canDelete = hasPermission(user, 'supplier_payments', 'delete');

  const fetchPayments = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter.supplier_id) params.set('supplier_id', filter.supplier_id);
      if (filter.start_date) params.set('start_date', filter.start_date);
      if (filter.end_date) params.set('end_date', filter.end_date);
      const res = await api.get(`/supplier-payments${params.toString() ? `?${params}` : ''}`);
      setPayments(res.data || []);
    } catch (e) {
      console.error('list supplier payments', e);
    } finally {
      setLoading(false);
    }
  };
  const fetchSuppliers = async () => {
    try {
      const res = await api.get('/suppliers');
      setSuppliers(res.data || []);
    } catch (e) { setSuppliers([]); }
  };
  const fetchOpenPOs = async (supplierId) => {
    if (!supplierId) { setOpenPOs([]); return; }
    try {
      const res = await api.get(`/supplier-payments/lookup/open-pos?supplier_id=${supplierId}`);
      setOpenPOs(res.data?.items || []);
    } catch (e) { setOpenPOs([]); }
  };

  useEffect(() => { fetchPayments(); fetchSuppliers(); }, []); // eslint-disable-line
  useEffect(() => { fetchPayments(); }, [filter.supplier_id, filter.start_date, filter.end_date]); // eslint-disable-line

  const openNew = () => {
    setEditingId(null);
    setForm(blankForm());
    setOpenPOs([]);
    setShowForm(true);
  };
  const openNewAdvance = () => {
    setEditingId(null);
    setForm({ ...blankForm(), payment_type: 'advance' });
    setOpenPOs([]);
    setShowForm(true);
  };
  const openEdit = async (p) => {
    setEditingId(p.id);
    setForm({
      ...blankForm(),
      ...p,
      lines: (p.lines || []).map((l) => ({ ...blankLine(), ...l })),
      received_date: (p.received_date || '').slice(0, 10),
      cheque_date: (p.cheque_date || '').slice(0, 10),
      transfer_date: (p.transfer_date || '').slice(0, 10),
    });
    await fetchOpenPOs(p.supplier_id);
    setShowForm(true);
  };
  const cancelForm = () => { setShowForm(false); setEditingId(null); };

  /* ── Advance Apply / Refund ──────────────────────────────────────── */
  const openApply = async (adv) => {
    setApplyAdv(adv);
    setApplyLines([{ po_id: '', amount_supplier_ccy: '' }]);
    await fetchOpenPOs(adv.supplier_id);
  };
  const closeApply = () => { setApplyAdv(null); setApplyLines([]); };
  const submitApply = async () => {
    if (!applyAdv) return;
    const lines = applyLines
      .filter((l) => l.po_id && parseFloat(l.amount_supplier_ccy) > 0)
      .map((l) => {
        const po = openPOs.find((p) => p.id === l.po_id);
        return {
          po_id: l.po_id,
          po_number: po?.po_number || '',
          po_currency: po?.currency || applyAdv.supplier_ccy || '',
          po_total: po?.total_amount || 0,
          amount_supplier_ccy: parseFloat(l.amount_supplier_ccy) || 0,
        };
      });
    if (!lines.length) return alert('Add at least one PO line to apply against.');
    try {
      await api.post(`/supplier-payments/${applyAdv.id}/apply`, { lines });
      closeApply();
      await fetchPayments();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Apply failed');
    }
  };
  const refundAdv = async (id) => {
    if (!window.confirm('Mark this advance as refunded? Only allowed when zero amount has been applied yet.')) return;
    try {
      await api.post(`/supplier-payments/${id}/refund`);
      await fetchPayments();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Refund failed');
    }
  };

  /* ── Field helpers ────────────────────────────────────────────────── */
  const onSupplier = async (e) => {
    const id = e.target.value;
    const s = suppliers.find((x) => x.id === id);
    setForm((f) => ({
      ...f,
      supplier_id: id,
      supplier_name: s?.name || '',
      supplier_ccy: s?.currency || 'USD',
      lines: [blankLine()],
    }));
    await fetchOpenPOs(id);
  };
  const onPickPO = (idx, poId) => {
    const po = openPOs.find((x) => x.id === poId);
    if (!po) return;
    setForm((f) => {
      const lines = f.lines.slice();
      lines[idx] = {
        ...lines[idx],
        po_id: po.id,
        po_number: po.po_number,
        po_currency: po.currency || f.supplier_ccy,
        po_total: po.total_amount || 0,
        amount_supplier_ccy: lines[idx].amount_supplier_ccy || (po.outstanding || ''),
      };
      return { ...f, lines };
    });
  };
  const onAddLine = () => setForm((f) => ({ ...f, lines: [...f.lines, blankLine()] }));
  const onRemoveLine = (idx) =>
    setForm((f) => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));

  /* ── Computed totals (display-only, mirrors backend `_compute_totals`) ── */
  const totals = useMemo(() => {
    const rate = parseFloat(form.exchange_rate) || 1;
    const fee = parseFloat(form.bank_fee_company_ccy) || 0;
    const sup = (form.lines || []).reduce((a, l) => a + (parseFloat(l.amount_supplier_ccy) || 0), 0);
    return {
      supplierTotal: sup,
      companyTotal: sup * rate,
      grandWithFee: sup * rate + fee,
    };
  }, [form.lines, form.exchange_rate, form.bank_fee_company_ccy]);

  /* ── Submit ──────────────────────────────────────────────────────── */
  const submit = async () => {
    if (!form.supplier_id) return alert('Pick a supplier.');
    if (!form.exchange_rate || parseFloat(form.exchange_rate) <= 0) return alert('Enter the exchange rate.');
    const isAdvance = form.payment_type === 'advance';
    if (isAdvance) {
      const amt = parseFloat(form.advance_amount_supplier_ccy) || 0;
      if (amt <= 0) return alert('Enter the advance amount.');
      // For an advance, PO lines are OPTIONAL — they represent immediate
      // application. Validate any populated lines but allow zero lines too.
      const filledLines = (form.lines || []).filter((l) => l.po_id || parseFloat(l.amount_supplier_ccy));
      if (filledLines.some((l) => !l.po_id || !parseFloat(l.amount_supplier_ccy))) {
        return alert('Each PO line on an advance needs both a PO and an amount.');
      }
      const splitTotal = filledLines.reduce((a, l) => a + (parseFloat(l.amount_supplier_ccy) || 0), 0);
      if (splitTotal > amt + 0.0001) {
        return alert(`PO splits (${splitTotal.toFixed(3)}) cannot exceed the advance amount (${amt.toFixed(3)}).`);
      }
    } else {
      if (!form.lines.length || form.lines.some((l) => !l.po_id || !l.amount_supplier_ccy)) {
        return alert('Each line must reference a PO and an amount.');
      }
    }
    const payload = {
      ...form,
      exchange_rate: parseFloat(form.exchange_rate),
      bank_fee_company_ccy: parseFloat(form.bank_fee_company_ccy) || 0,
      company_ccy: companyCcy,
      payment_type: isAdvance ? 'advance' : 'po_payment',
      advance_amount_supplier_ccy: isAdvance ? (parseFloat(form.advance_amount_supplier_ccy) || 0) : null,
      lines: (isAdvance ? (form.lines || []).filter((l) => l.po_id && parseFloat(l.amount_supplier_ccy)) : form.lines).map((l) => ({
        ...l,
        amount_supplier_ccy: parseFloat(l.amount_supplier_ccy) || 0,
      })),
    };
    try {
      if (editingId) {
        await api.put(`/supplier-payments/${editingId}`, payload);
      } else {
        await api.post('/supplier-payments', payload);
      }
      setShowForm(false);
      setEditingId(null);
      await fetchPayments();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Save failed');
    }
  };
  const remove = async (id) => {
    if (!window.confirm('Delete this supplier payment? PO balances will be recomputed.')) return;
    try {
      await api.delete(`/supplier-payments/${id}`);
      await fetchPayments();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Delete failed');
    }
  };

  /* ── Excel export — one row per payment + a Lines sheet ───────── */
  const exportExcel = () => {
    if (!payments.length) return alert('No supplier payments to export.');
    const summary = payments.map((p) => ({
      Date: p.received_date || '',
      Supplier: p.supplier_name || '',
      Mode: p.payment_mode || '',
      Bank: p.bank_name || '',
      [`Supplier Amt (${p.supplier_ccy || 'CCY'})`]: Number((p.total_supplier_ccy || 0).toFixed(3)),
      'FX Rate': Number((p.exchange_rate || 0).toFixed(6)),
      [`Equiv (${companyCcy})`]: Number((p.total_company_ccy || 0).toFixed(3)),
      [`Bank Fee (${companyCcy})`]: Number((p.bank_fee_company_ccy || 0).toFixed(3)),
      [`Total (${companyCcy})`]: Number((p.total_with_fee_company_ccy || 0).toFixed(3)),
      'Bill #': p.supplier_bill_no || '',
      POs: (p.lines || []).map((l) => l.po_number).join(', '),
      Notes: p.notes || '',
    }));
    const lineRows = [];
    payments.forEach((p) => {
      (p.lines || []).forEach((l) => {
        lineRows.push({
          Date: p.received_date || '',
          Supplier: p.supplier_name || '',
          'PO #': l.po_number,
          [`Amount (${p.supplier_ccy || 'CCY'})`]: Number((l.amount_supplier_ccy || 0).toFixed(3)),
          [`Equivalent (${companyCcy})`]: Number(((l.amount_supplier_ccy || 0) * (p.exchange_rate || 0)).toFixed(3)),
          Note: l.note || '',
        });
      });
    });
    const wb = XLSX.utils.book_new();
    const wsSum = XLSX.utils.json_to_sheet(summary);
    const wsLines = XLSX.utils.json_to_sheet(lineRows);
    XLSX.utils.book_append_sheet(wb, wsSum, 'Supplier Payments');
    XLSX.utils.book_append_sheet(wb, wsLines, 'PO Lines');
    XLSX.writeFile(wb, `supplier_payments_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  /* ── PDF export — letterhead-styled iframe (matches Sales Reports) ─ */
  const exportPDF = () => {
    if (!payments.length) return alert('No supplier payments to export.');
    const dateRangeLabel = (filter.start_date || filter.end_date)
      ? `${filter.start_date || '—'} → ${filter.end_date || '—'}`
      : 'All dates';
    const supLabel = filter.supplier_id
      ? (suppliers.find((s) => s.id === filter.supplier_id)?.name || '—')
      : 'All suppliers';

    const fmtMoney = (n, dp = 3) => (parseFloat(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: dp });

    const grandSupplier = {};
    let grandCompany = 0;
    let grandWithFee = 0;
    payments.forEach((p) => {
      const ccy = p.supplier_ccy || 'CCY';
      grandSupplier[ccy] = (grandSupplier[ccy] || 0) + (p.total_supplier_ccy || 0);
      grandCompany += (p.total_company_ccy || 0);
      grandWithFee += (p.total_with_fee_company_ccy || 0);
    });

    const headerHtml = `
      <div class="lh">
        ${companySettings?.logo_url ? `<img class="lh-logo" src="${companySettings.logo_url}" alt="" />` : ''}
        <div class="lh-meta">
          <div class="lh-name">${(companySettings?.name || '').replace(/</g, '&lt;')}</div>
          ${companySettings?.tax_id ? `<div class="lh-line">VAT: ${companySettings.tax_id}</div>` : ''}
          ${companySettings?.cr_number ? `<div class="lh-line">CR: ${companySettings.cr_number}</div>` : ''}
        </div>
        ${companySettings?.document_header_url ? `<img class="lh-banner" src="${companySettings.document_header_url}" alt="" />` : ''}
      </div>`;
    const bodyRows = payments.map((p) => `
      <tr>
        <td>${p.received_date || ''}</td>
        <td>${(p.supplier_name || '').replace(/</g, '&lt;')}</td>
        <td>${p.payment_mode || ''}${p.bank_name ? ` · ${p.bank_name}` : ''}</td>
        <td class="r">${fmtMoney(p.total_supplier_ccy)} ${p.supplier_ccy || ''}</td>
        <td class="r">${(p.exchange_rate || 0).toFixed(4)}</td>
        <td class="r">${fmtMoney(p.total_company_ccy)}</td>
        <td class="r">${fmtMoney(p.bank_fee_company_ccy)}</td>
        <td class="r">${fmtMoney(p.total_with_fee_company_ccy)}</td>
        <td>${p.supplier_bill_no || '—'}</td>
        <td>${(p.lines || []).map((l) => l.po_number).join(', ')}</td>
      </tr>`).join('');
    const totalsHtml = `
      <tr class="totals">
        <td colspan="3">TOTAL (${payments.length} payment${payments.length === 1 ? '' : 's'})</td>
        <td class="r">${Object.entries(grandSupplier).map(([c, v]) => `${fmtMoney(v)} ${c}`).join('<br/>')}</td>
        <td></td>
        <td class="r">${fmtMoney(grandCompany)}</td>
        <td class="r"></td>
        <td class="r">${fmtMoney(grandWithFee)}</td>
        <td></td><td></td>
      </tr>`;
    const html = `
      <!doctype html><html><head><title>Supplier Payments</title>
      <style>
        * { box-sizing: border-box; }
        body { margin: 0; font-family: 'Segoe UI', Arial, sans-serif; color: #111; }
        .page { padding: 14mm 12mm; }
        .lh { display: flex; align-items: center; gap: 12px; border-bottom: 2px solid #1d4ed8; padding-bottom: 8px; margin-bottom: 10px; }
        .lh-logo { max-height: 56px; max-width: 130px; object-fit: contain; }
        .lh-banner { max-height: 56px; max-width: 240px; margin-left: auto; object-fit: contain; }
        .lh-name { font-size: 16pt; font-weight: 800; color: #0f172a; }
        .lh-line { font-size: 9pt; color: #64748b; }
        .title-row { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 6px; }
        .doc-title { font-size: 14pt; font-weight: 700; color: #0f172a; }
        .filters { font-size: 9pt; color: #475569; }
        table { width: 100%; border-collapse: collapse; font-size: 9pt; margin-top: 4px; }
        th, td { border: 1px solid #cbd5e1; padding: 5px 7px; text-align: left; }
        th { background: #f1f5f9; font-size: 8pt; color: #475569; text-transform: uppercase; letter-spacing: 0.4px; }
        td.r, th.r { text-align: right; font-variant-numeric: tabular-nums; }
        tr.totals td { background: #eff6ff; font-weight: 700; font-size: 10pt; }
        .meta { font-size: 8pt; color: #94a3b8; margin-top: 10px; text-align: right; }
        @media print { @page { size: A4 landscape; margin: 8mm; } }
      </style></head>
      <body>
        <div class="page">
          ${headerHtml}
          <div class="title-row">
            <div class="doc-title">Supplier Payments Report</div>
            <div class="filters">Supplier: ${supLabel} · Period: ${dateRangeLabel}</div>
          </div>
          <table>
            <thead>
              <tr>
                <th>Date</th><th>Supplier</th><th>Mode</th>
                <th class="r">Supplier Amt</th><th class="r">FX</th>
                <th class="r">${companyCcy} Equiv</th><th class="r">Bank Fee</th><th class="r">Total ${companyCcy}</th>
                <th>Bill #</th><th>POs</th>
              </tr>
            </thead>
            <tbody>${bodyRows}${totalsHtml}</tbody>
          </table>
          <div class="meta">Generated ${new Date().toLocaleString()}</div>
        </div>
      </body></html>`;

    const iframe = document.createElement('iframe');
    Object.assign(iframe.style, { position: 'fixed', right: 0, bottom: 0, width: 0, height: 0, border: 0 });
    document.body.appendChild(iframe);
    const doc = iframe.contentDocument;
    doc.open(); doc.write(html); doc.close();
    const fire = () => {
      const imgs = Array.from(doc.images || []);
      const waiters = imgs.map((img) => img.complete && img.naturalWidth > 0
        ? Promise.resolve()
        : new Promise((res) => {
            const done = () => res();
            img.addEventListener('load', done, { once: true });
            img.addEventListener('error', done, { once: true });
            setTimeout(done, 3000);
          })
      );
      Promise.all(waiters).then(() => {
        try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* noop */ }
        setTimeout(() => { try { document.body.removeChild(iframe); } catch { /* noop */ } }, 1500);
      });
    };
    setTimeout(fire, 200);
  };

  return (
    <div className="px-4 py-5" data-testid="supplier-payments-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">🏦 Supplier Payments</h1>
          <p className="text-xs text-slate-500">Outgoing wires / cheques to suppliers. One payment can cover multiple POs.</p>
        </div>
        <div className="flex border-b border-slate-200">
          <button
            onClick={() => setActiveTab('payments')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === 'payments' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="sp-tab-payments"
          >
            💸 Payments
          </button>
          <button
            onClick={() => setActiveTab('ledger')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === 'ledger' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="sp-tab-ledger"
          >
            📒 Supplier Ledger
          </button>
          <button
            onClick={() => setActiveTab('creditors')}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${activeTab === 'creditors' ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
            data-testid="sp-tab-creditors"
          >
            📊 Creditors Aging
          </button>
        </div>
      </div>

      {activeTab === 'ledger' ? (
        <SupplierLedgerTab suppliers={suppliers} />
      ) : activeTab === 'creditors' ? (
        <CreditorsAgingTab />
      ) : (
        <>
      <div className="flex flex-wrap items-end justify-end gap-2 mb-4">
        <div className="flex gap-2 flex-wrap">
          <select value={filter.supplier_id} onChange={(e) => setFilter({ ...filter, supplier_id: e.target.value })} className="border rounded px-2 py-1.5 text-sm" data-testid="sp-filter-supplier">
            <option value="">All suppliers</option>
            {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input type="date" value={filter.start_date} onChange={(e) => setFilter({ ...filter, start_date: e.target.value })} className="border rounded px-2 py-1.5 text-sm" />
          <input type="date" value={filter.end_date} onChange={(e) => setFilter({ ...filter, end_date: e.target.value })} className="border rounded px-2 py-1.5 text-sm" />
          <button onClick={exportExcel} className="px-3 py-1.5 rounded border border-emerald-600 text-emerald-700 hover:bg-emerald-50 text-sm" data-testid="sp-export-excel">📊 Excel</button>
          <button onClick={exportPDF} className="px-3 py-1.5 rounded border border-rose-600 text-rose-700 hover:bg-rose-50 text-sm" data-testid="sp-export-pdf">📄 PDF</button>
          {canCreate && (
            <>
              <button onClick={openNew} className="px-3 py-1.5 rounded bg-emerald-600 text-white text-sm" data-testid="sp-new-btn">+ New Payment</button>
              <button onClick={openNewAdvance} className="px-3 py-1.5 rounded bg-amber-600 text-white text-sm" data-testid="sp-new-advance-btn">💸 Record Advance</button>
            </>
          )}
        </div>
      </div>

      <div className="bg-white border rounded-lg overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr>
              <th className="px-3 py-2 text-left">Date</th>
              <th className="px-3 py-2 text-left">Supplier</th>
              <th className="px-3 py-2 text-left">Mode</th>
              <th className="px-3 py-2 text-right">Supplier Amt</th>
              <th className="px-3 py-2 text-right">FX</th>
              <th className="px-3 py-2 text-right">{companyCcy} Equiv</th>
              <th className="px-3 py-2 text-right">Bank Fee ({companyCcy})</th>
              <th className="px-3 py-2 text-right">Total ({companyCcy})</th>
              <th className="px-3 py-2 text-left">Bill #</th>
              <th className="px-3 py-2 text-left">POs</th>
              <th className="px-3 py-2 text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="text-center py-6 text-slate-500">Loading…</td></tr>
            )}
            {!loading && payments.length === 0 && (
              <tr><td colSpan={11} className="text-center py-6 text-slate-500">No supplier payments yet.</td></tr>
            )}
            {!loading && payments.map((p) => (
              <tr key={p.id} className="border-t hover:bg-blue-50/30" data-testid={`sp-row-${p.id}`}>
                <td className="px-3 py-2">{formatDateDMY(p.received_date)}</td>
                <td className="px-3 py-2 font-medium">
                  {p.supplier_name}
                  {p.payment_type === 'advance' && (
                    <span className={`ml-2 inline-block px-2 py-0.5 rounded text-[10px] font-semibold ${p.advance_status === 'refunded' ? 'bg-slate-200 text-slate-600' : p.advance_status === 'applied' ? 'bg-emerald-100 text-emerald-800' : p.advance_status === 'partially_applied' ? 'bg-amber-100 text-amber-800' : 'bg-amber-200 text-amber-900'}`} data-testid={`sp-advance-badge-${p.id}`}>
                      ADV · {(p.advance_status || 'unapplied').replace('_', ' ')}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2">{p.payment_mode}{p.payment_mode === 'Bank Transfer' && p.bank_name ? ` — ${p.bank_name}` : ''}</td>
                <td className="px-3 py-2 text-right font-mono">{(p.total_supplier_ccy || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 })} <span className="text-xs text-slate-400">{p.supplier_ccy}</span></td>
                <td className="px-3 py-2 text-right font-mono">{(p.exchange_rate || 0).toFixed(4)}</td>
                <td className="px-3 py-2 text-right font-mono">{formatAmount(p.total_company_ccy, { showSymbol: false })}</td>
                <td className="px-3 py-2 text-right font-mono">{formatAmount(p.bank_fee_company_ccy, { showSymbol: false })}</td>
                <td className="px-3 py-2 text-right font-mono font-semibold">{formatAmount(p.total_with_fee_company_ccy, { showSymbol: false })}</td>
                <td className="px-3 py-2 text-slate-600">{p.supplier_bill_no || '—'}</td>
                <td className="px-3 py-2 text-xs">{(p.lines || []).map((l) => l.po_number).join(', ')}</td>
                <td className="px-3 py-2 text-center whitespace-nowrap">
                  <button onClick={() => setViewing(p)} className="text-slate-700 text-xs mr-2" data-testid={`sp-view-${p.id}`}>👁 View</button>
                  {p.payment_type === 'advance' && p.advance_status !== 'refunded' && p.advance_status !== 'applied' && canEdit && (
                    <button onClick={() => openApply(p)} className="text-amber-700 text-xs mr-2 font-semibold" data-testid={`sp-apply-${p.id}`}>🔗 Apply to PO</button>
                  )}
                  {p.payment_type === 'advance' && p.advance_status === 'unapplied' && canDelete && (
                    <button onClick={() => refundAdv(p.id)} className="text-rose-700 text-xs mr-2" data-testid={`sp-refund-${p.id}`}>↩ Refund</button>
                  )}
                  {canEdit && <button onClick={() => openEdit(p)} className="text-blue-700 text-xs mr-2" data-testid={`sp-edit-${p.id}`}>✏️ Edit</button>}
                  {canDelete && <button onClick={() => remove(p.id)} className="text-red-700 text-xs">🗑 Delete</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        </>
      )}

      {/* New / Edit modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={cancelForm}>
          <div className="bg-white rounded-lg max-w-4xl w-full my-6 shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="sp-form-modal">
            <div className="px-5 py-3 border-b flex items-center justify-between sticky top-0 bg-white rounded-t-lg">
              <h2 className="font-semibold text-lg">
                {editingId ? '✏️ Edit Supplier Payment' : form.payment_type === 'advance' ? '💸 Record Supplier Advance' : '+ New Supplier Payment'}
              </h2>
              <button onClick={cancelForm} className="text-2xl text-slate-500">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Supplier *</label>
                  <select value={form.supplier_id} onChange={onSupplier} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="sp-supplier">
                    <option value="">— Choose —</option>
                    {suppliers.map((s) => <option key={s.id} value={s.id}>{s.name}{s.currency ? ` (${s.currency})` : ''}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Date *</label>
                  <input type="date" value={form.received_date} onChange={(e) => setForm({ ...form, received_date: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="sp-date" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Mode *</label>
                  <select value={form.payment_mode} onChange={(e) => setForm({ ...form, payment_mode: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="sp-mode">
                    {['Cash', 'Bank Transfer', 'Cheque', 'Card', 'Other'].map((m) => <option key={m}>{m}</option>)}
                  </select>
                </div>
              </div>

              {form.payment_mode === 'Bank Transfer' && (
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Bank Name</label>
                    <input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="sp-bank" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Transfer Date</label>
                    <input type="date" value={form.transfer_date} onChange={(e) => setForm({ ...form, transfer_date: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" />
                  </div>
                </div>
              )}
              {form.payment_mode === 'Cheque' && (
                <div className="grid md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Cheque No</label>
                    <input value={form.cheque_no} onChange={(e) => setForm({ ...form, cheque_no: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Cheque Date</label>
                    <input type="date" value={form.cheque_date} onChange={(e) => setForm({ ...form, cheque_date: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-700 mb-1">Bank Name</label>
                    <input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" />
                  </div>
                </div>
              )}

              <div className="grid md:grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Supplier Currency</label>
                  <input value={form.supplier_ccy} onChange={(e) => setForm({ ...form, supplier_ccy: e.target.value.toUpperCase() })} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="sp-supplier-ccy" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Exchange Rate <span className="text-slate-400 font-normal">({form.supplier_ccy || '—'} → {companyCcy})</span> *</label>
                  <input type="number" step="0.0001" value={form.exchange_rate} onChange={(e) => setForm({ ...form, exchange_rate: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" placeholder="e.g. 0.3770" data-testid="sp-rate" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Bank Fee ({companyCcy})</label>
                  <input type="number" step="0.001" value={form.bank_fee_company_ccy} onChange={(e) => setForm({ ...form, bank_fee_company_ccy: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" data-testid="sp-bank-fee" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">Supplier Bill #</label>
                  <input value={form.supplier_bill_no} onChange={(e) => setForm({ ...form, supplier_bill_no: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" placeholder="Optional" data-testid="sp-bill-no" />
                </div>
              </div>

              {/* Advance amount — shown only when recording an advance.
                  PO splits are skipped here; they're added later via the
                  "Apply to PO" action on the row. */}
              {form.payment_type === 'advance' && (
                <div className="border rounded border-amber-300 bg-amber-50" data-testid="sp-advance-section">
                  <div className="px-3 py-2 bg-amber-100 text-sm font-semibold text-amber-900">
                    💸 Advance Payment
                    <span className="ml-2 text-[11px] font-normal opacity-70">No PO is consumed yet. You can apply this amount to one or more POs later from the table.</span>
                  </div>
                  <div className="p-3 grid md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-slate-600 mb-1">Advance Amount ({form.supplier_ccy}) *</label>
                      <input
                        type="number"
                        step="0.001"
                        value={form.advance_amount_supplier_ccy}
                        onChange={(e) => setForm((f) => ({ ...f, advance_amount_supplier_ccy: e.target.value }))}
                        className="w-full border rounded px-2 py-1.5 text-sm font-mono"
                        data-testid="sp-advance-amount"
                      />
                    </div>
                    <div className="md:col-span-2 text-xs text-slate-700 self-end pb-1">
                      {(parseFloat(form.advance_amount_supplier_ccy) || 0).toFixed(3)} {form.supplier_ccy}
                      {form.exchange_rate && (
                        <> · ≈ <b>{((parseFloat(form.advance_amount_supplier_ccy) || 0) * parseFloat(form.exchange_rate)).toFixed(3)} {companyCcy}</b></>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* PO lines — shown for BOTH PO payment AND advance.
                  For an advance, filling lines here immediately applies
                  the advance against those POs at creation time. Leave
                  blank to record the advance as unapplied and use the
                  '🔗 Apply to PO' button later. */}
              <div className="border rounded">
                <div className="px-3 py-2 bg-slate-50 flex items-center justify-between">
                  <div className="text-sm font-semibold text-slate-800">
                    📑 PO Lines ({form.lines.length})
                    {form.payment_type === 'advance' && (
                      <span className="ml-2 text-[11px] font-normal text-amber-700">Optional — pick PO(s) to apply this advance immediately, or leave blank to apply later.</span>
                    )}
                  </div>
                  {form.supplier_id ? (
                    <button onClick={onAddLine} className="text-xs px-2 py-1 rounded border text-blue-700 border-blue-300" data-testid="sp-add-line">+ Add another PO</button>
                  ) : (
                    <span className="text-xs text-slate-500">Pick a supplier first.</span>
                  )}
                </div>
                <div className="divide-y">
                  {form.lines.map((line, idx) => (
                    <div key={idx} className="p-3 grid md:grid-cols-12 gap-2 items-end" data-testid={`sp-line-${idx}`}>
                      <div className="md:col-span-5">
                        <label className="block text-xs font-semibold text-slate-600 mb-1">PO *</label>
                        <select value={line.po_id} onChange={(e) => onPickPO(idx, e.target.value)} className="w-full border rounded px-2 py-1.5 text-sm" data-testid={`sp-line-po-${idx}`}>
                          <option value="">— Pick a PO —</option>
                          {openPOs.map((po) => (
                            <option key={po.id} value={po.id}>
                              {po.po_number} · {formatDateDMY(po.po_date)} · {po.currency} {(po.total_amount || 0).toFixed(2)} · outstanding {po.outstanding}
                            </option>
                          ))}
                        </select>
                        {line.po_id && <div className="text-[10px] text-slate-400 mt-0.5">Currency: {line.po_currency} · Gross: {line.po_total}</div>}
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-xs font-semibold text-slate-600 mb-1">Amount ({form.supplier_ccy}) *</label>
                        <input type="number" step="0.001" value={line.amount_supplier_ccy}
                          onChange={(e) => setForm((f) => {
                            const lines = f.lines.slice();
                            lines[idx] = { ...lines[idx], amount_supplier_ccy: e.target.value };
                            return { ...f, lines };
                          })}
                          className="w-full border rounded px-2 py-1.5 text-sm"
                          data-testid={`sp-line-amt-${idx}`}
                        />
                      </div>
                      <div className="md:col-span-3">
                        <label className="block text-xs font-semibold text-slate-600 mb-1">Note</label>
                        <input value={line.note} onChange={(e) => setForm((f) => {
                          const lines = f.lines.slice();
                          lines[idx] = { ...lines[idx], note: e.target.value };
                          return { ...f, lines };
                        })} className="w-full border rounded px-2 py-1.5 text-sm" />
                      </div>
                      <div className="md:col-span-1 text-right">
                        {form.lines.length > 1 && (
                          <button onClick={() => onRemoveLine(idx)} className="text-rose-600 text-xs" data-testid={`sp-line-del-${idx}`}>🗑</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Live totals */}
              <div className="rounded border bg-slate-50 p-3 text-sm grid md:grid-cols-3 gap-3" data-testid="sp-totals">
                <div>
                  <div className="text-xs text-slate-500">Supplier-Currency Total</div>
                  <div className="font-bold text-slate-900">{form.supplier_ccy} {totals.supplierTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">{companyCcy} Equivalent</div>
                  <div className="font-bold text-slate-900">{formatAmount(totals.companyTotal)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Total + Bank Fee</div>
                  <div className="font-bold text-emerald-700">{formatAmount(totals.grandWithFee)}</div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-700 mb-1">Notes</label>
                <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>
            </div>
            <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2 rounded-b-lg">
              <button onClick={cancelForm} className="px-3 py-1.5 rounded border text-sm">Cancel</button>
              <button onClick={submit} className="px-3 py-1.5 rounded bg-emerald-600 text-white text-sm" data-testid="sp-save-btn">
                {editingId ? 'Update Payment' : 'Save Payment'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Apply Advance modal */}
      {applyAdv && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={closeApply} data-testid="sp-apply-modal">
          <div className="bg-white rounded-lg max-w-3xl w-full my-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b bg-amber-50 flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-lg">🔗 Apply Advance to PO(s)</h2>
                <div className="text-xs text-slate-600 mt-0.5">
                  {applyAdv.supplier_name} · Gross <b>{(applyAdv.advance_amount_supplier_ccy || 0).toFixed(3)} {applyAdv.supplier_ccy}</b>
                  {' · '}Already applied <b>{(applyAdv.lines || []).reduce((a, l) => a + parseFloat(l.amount_supplier_ccy || 0), 0).toFixed(3)}</b>
                </div>
              </div>
              <button onClick={closeApply} className="text-2xl text-slate-500">✕</button>
            </div>
            <div className="p-5 space-y-3">
              {applyLines.map((line, idx) => (
                <div key={idx} className="grid md:grid-cols-12 gap-2 items-end" data-testid={`sp-apply-line-${idx}`}>
                  <div className="md:col-span-7">
                    <label className="block text-xs font-semibold text-slate-600 mb-1">PO *</label>
                    <select
                      value={line.po_id}
                      onChange={(e) => setApplyLines((ls) => {
                        const next = ls.slice();
                        next[idx] = { ...next[idx], po_id: e.target.value };
                        return next;
                      })}
                      className="w-full border rounded px-2 py-1.5 text-sm"
                      data-testid={`sp-apply-po-${idx}`}
                    >
                      <option value="">— Pick a PO —</option>
                      {openPOs.map((po) => (
                        <option key={po.id} value={po.id}>
                          {po.po_number} · {formatDateDMY(po.po_date)} · {po.currency} {(po.total_amount || 0).toFixed(2)} · outstanding {po.outstanding}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-4">
                    <label className="block text-xs font-semibold text-slate-600 mb-1">Amount ({applyAdv.supplier_ccy}) *</label>
                    <input
                      type="number"
                      step="0.001"
                      value={line.amount_supplier_ccy}
                      onChange={(e) => setApplyLines((ls) => {
                        const next = ls.slice();
                        next[idx] = { ...next[idx], amount_supplier_ccy: e.target.value };
                        return next;
                      })}
                      className="w-full border rounded px-2 py-1.5 text-sm font-mono"
                      data-testid={`sp-apply-amt-${idx}`}
                    />
                  </div>
                  <div className="md:col-span-1 text-right">
                    {applyLines.length > 1 && (
                      <button onClick={() => setApplyLines((ls) => ls.filter((_, i) => i !== idx))} className="text-rose-600 text-xs">🗑</button>
                    )}
                  </div>
                </div>
              ))}
              <button
                onClick={() => setApplyLines((ls) => [...ls, { po_id: '', amount_supplier_ccy: '' }])}
                className="text-xs text-blue-700 border border-blue-300 rounded px-2 py-1"
                data-testid="sp-apply-add-line"
              >
                + Add another PO
              </button>
              <div className="text-xs text-slate-500 pt-2">
                Total to apply now:{' '}
                <b>
                  {applyLines.reduce((a, l) => a + (parseFloat(l.amount_supplier_ccy) || 0), 0).toFixed(3)} {applyAdv.supplier_ccy}
                </b>
              </div>
            </div>
            <div className="px-5 py-3 border-t flex justify-end gap-2">
              <button onClick={closeApply} className="px-4 py-1.5 rounded border text-sm">Cancel</button>
              <button onClick={submitApply} className="px-4 py-1.5 rounded bg-amber-600 text-white text-sm" data-testid="sp-apply-submit">
                🔗 Apply
              </button>
            </div>
          </div>
        </div>
      )}
      {viewing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={() => setViewing(null)} data-testid="sp-view-modal">
          <div className="bg-white rounded-lg max-w-3xl w-full my-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-3 border-b flex items-center justify-between">
              <h2 className="font-semibold text-lg">🏦 Supplier Payment #{viewing.id?.slice(0, 8)}</h2>
              <button onClick={() => setViewing(null)} className="text-2xl text-slate-500">✕</button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-slate-500">Supplier:</span> <b>{viewing.supplier_name}</b></div>
                <div><span className="text-slate-500">Date:</span> {formatDateDMY(viewing.received_date)}</div>
                <div><span className="text-slate-500">Mode:</span> {viewing.payment_mode}{viewing.bank_name ? ` — ${viewing.bank_name}` : ''}</div>
                <div><span className="text-slate-500">Bill #:</span> {viewing.supplier_bill_no || '—'}</div>
                <div><span className="text-slate-500">Exchange Rate:</span> {viewing.exchange_rate}</div>
                <div><span className="text-slate-500">Bank Fee:</span> {formatAmount(viewing.bank_fee_company_ccy)}</div>
              </div>
              <div className="border rounded">
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="px-2 py-1 text-left">PO</th>
                      <th className="px-2 py-1 text-right">Amount ({viewing.supplier_ccy})</th>
                      <th className="px-2 py-1 text-right">Equivalent ({viewing.company_ccy})</th>
                      <th className="px-2 py-1 text-left">Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(viewing.lines || []).map((l, i) => (
                      <tr key={i} className="border-t">
                        <td className="px-2 py-1">{l.po_number}</td>
                        <td className="px-2 py-1 text-right font-mono">{(l.amount_supplier_ccy || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}</td>
                        <td className="px-2 py-1 text-right font-mono">{formatAmount((l.amount_supplier_ccy || 0) * (viewing.exchange_rate || 0), { showSymbol: false })}</td>
                        <td className="px-2 py-1 text-slate-600">{l.note || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t bg-slate-50 font-bold">
                      <td className="px-2 py-1">Totals</td>
                      <td className="px-2 py-1 text-right font-mono">{(viewing.total_supplier_ccy || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 3 })}</td>
                      <td className="px-2 py-1 text-right font-mono">{formatAmount(viewing.total_company_ccy, { showSymbol: false })}</td>
                      <td className="px-2 py-1"></td>
                    </tr>
                    <tr className="border-t bg-emerald-50 font-bold">
                      <td className="px-2 py-1">Total + Bank Fee</td>
                      <td colSpan={2} className="px-2 py-1 text-right font-mono text-emerald-700">{formatAmount(viewing.total_with_fee_company_ccy)}</td>
                      <td className="px-2 py-1"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {viewing.notes && (
                <div className="text-xs text-slate-600"><span className="font-semibold">Notes:</span> {viewing.notes}</div>
              )}
            </div>
            <div className="px-5 py-3 border-t bg-slate-50 flex justify-end gap-2">
              <button onClick={() => setViewing(null)} className="px-3 py-1.5 rounded bg-slate-800 text-white text-sm">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupplierPayments;
